// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  LockOpenIcon,
  EyeIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Context from "../../context/Context";
import { deleteForm, fetchForms } from "../../context/forms/FormsAction";
import { resolveMediaUrl, URL, URL_FRONT } from "../../service/Service";
import Header from "../../components/TitleAndSubtitle";
import Spinner from "../../components/Spinner";
import Breadcrumb from "../../components/Breadcrumb";
import SharePublicLink from "../../components/SharePublicLink";
import {
  formatExpirationDate,
  isFormExpired,
} from "../../utils/formExpiration";

const ROWS_PER_PAGE = 10;

function formatDate(timestamp) {
  if (!timestamp) return "Sem data";

  try {
    const raw = String(timestamp).trim();
    let utcDate = null;

    // Formato usado atualmente pelo euForms: dd:MM:yyyy HH:mm[:ss]
    let match = raw.match(
      /^(\d{2}):(\d{2}):(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/,
    );

    if (match) {
      const [, day, month, year, hour, minute, second = "0"] = match;

      utcDate = new Date(
        Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second),
        ),
      );
    }

    // Também suporta yyyy-MM-dd HH:mm[:ss]
    if (!utcDate) {
      match = raw.match(
        /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/,
      );

      if (match) {
        const [, year, month, day, hour, minute, second = "0"] = match;

        utcDate = new Date(
          Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second),
          ),
        );
      }
    }

    // ISO com Z/offset, ou ISO sem timezone (tratado como UTC).
    if (!utcDate) {
      const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
      const isoValue = hasTimezone ? raw : `${raw.replace(" ", "T")}Z`;

      const parsed = new Date(isoValue);

      if (!Number.isNaN(parsed.getTime())) {
        utcDate = parsed;
      }
    }

    if (!utcDate || Number.isNaN(utcDate.getTime())) {
      return raw;
    }

    /*
     * A BD guarda em UTC.
     * O browser apresenta automaticamente no timezone local do utilizador.
     * Em Portugal continental:
     * - verão: UTC+1
     * - inverno: UTC
     */
    return new Intl.DateTimeFormat("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .format(utcDate)
      .replace(",", " ·");
  } catch {
    return String(timestamp);
  }
}

function getInitials(name) {
  if (!name) return "F";

  const parts = String(name).trim().split(" ").filter(Boolean).slice(0, 2);

  return parts.map((part) => part[0]?.toUpperCase()).join("") || "F";
}

function AdminFormThumbnail({ form }) {
  const [imageUrl, setImageUrl] = useState("");
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let active = true;

    setImageUrl("");
    setImageFailed(false);

    if (!form?.logo || !form?.id) {
      return () => {
        active = false;
      };
    }

    /*
     * O FormDTO guarda a referência do logo, mas a aplicação já utiliza
     * este endpoint para obter a URL temporária da imagem do formulário.
     */
    fetch(`${URL}/getPresignedUrl/${form.id}`, {
      headers: {
        Accept: "text/plain",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Erro HTTP ${response.status}`);
        }

        return response.text();
      })
      .then((url) => {
        if (active && url) {
          setImageUrl(resolveMediaUrl(url));
        }
      })
      .catch(() => {
        if (active) {
          setImageFailed(true);
        }
      });

    return () => {
      active = false;
    };
  }, [form?.id, form?.logo]);

  if (!form?.logo || !imageUrl || imageFailed) {
    return (
      <span
        className="flex h-14 w-16 flex-none items-center justify-center overflow-hidden rounded-xl border border-emerald-100 bg-emerald-50 text-sm font-extrabold text-emerald-700 shadow-sm"
        title={
          form?.logo
            ? "Imagem do formulário indisponível"
            : "Formulário sem imagem"
        }
      >
        {getInitials(form?.name)}
      </span>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`Imagem do formulário ${form?.name || ""}`.trim()}
      className="h-14 w-16 flex-none rounded-xl border border-slate-200 bg-white object-cover shadow-sm"
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
}

const AdminForms = () => {
  const { state, dispatch } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedFormId = new URLSearchParams(location.search).get("form") || "";
  const { loading, error, data } = state.forms;
  const [searchTerm, setSearchTerm] = useState(selectedFormId);
  const [currentPage, setCurrentPage] = useState(1);
  const [formIdToDelete, setFormIdToDelete] = useState(null);
  const [expandedFormId, setExpandedFormId] = useState(null);
  const [emails, setEmails] = useState({});
  const [notice, setNotice] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sharingForm, setSharingForm] = useState(null);
  const [newFormName, setNewFormName] = useState("");
  const [creating, setCreating] = useState(false);

  const forms = useMemo(() => Object.values(data || {}).reverse(), [data]);

  useEffect(() => {
    setSearchTerm(selectedFormId);
    setCurrentPage(1);
  }, [selectedFormId]);
  const filteredForms = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return forms;
    return forms.filter(
      (form) =>
        String(form.name || "")
          .toLowerCase()
          .includes(query) ||
        String(form.userEmail || "")
          .toLowerCase()
          .includes(query) ||
        String(form.id || "")
          .toLowerCase()
          .includes(query) ||
        String(form.apiKey || "")
          .toLowerCase()
          .includes(query),
    );
  }, [forms, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredForms.length / ROWS_PER_PAGE),
  );
  const currentRows = filteredForms.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  useEffect(() => {
    fetchForms(`${URL}/api/admin/forms`, {}, dispatch);
  }, [dispatch]);

  useEffect(() => {
    setEmails(
      Object.fromEntries(forms.map((form) => [form.id, form.userEmail || ""])),
    );
  }, [forms]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pages = [
    { name: "Dashboard", href: "/admin/dashboard", current: false },
    { name: "Formulários", href: "/admin/forms", current: true },
  ];

  const handleCopy = async (value, label) => {
    await navigator.clipboard.writeText(value);
    setNotice({ type: "success", message: `${label} copiado.` });
  };

  const handleAction = (value, formId) => {
    if (!value) return;
    if (value === "public") window.open(`/clientforms/${formId}`, "_blank");
    if (value === "fields") navigate(`/admin/forms/${formId}/formwizard/1`);
    if (value === "settings") navigate(`/admin/forms/${formId}/formwizard/2`);
    if (value === "replies") navigate(`/admin/forms/${formId}/replies`);
    if (value === "copy-url")
      handleCopy(`${URL_FRONT}/clientforms/${formId}`, "URL");
    if (value === "copy-id") handleCopy(formId, "Identificador");
    if (value === "delete") setFormIdToDelete(formId);
  };

  const changeFormStatus = async (form) => {
    const nextStatus = form.status === "PAUSED" ? "ACTIVE" : "PAUSED";
    try {
      const response = await fetch(`${URL}/api/admin/forms/${form.id}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!response.ok) throw new Error(await response.text());
      await fetchForms(`${URL}/api/admin/forms`, {}, dispatch);
      setNotice({
        type: "success",
        message:
          nextStatus === "PAUSED"
            ? "Formulário bloqueado. Deixou de aceitar respostas."
            : "Formulário desbloqueado.",
      });
    } catch (error) {
      setNotice({
        type: "error",
        message: error.message || "Não foi possível alterar o estado.",
      });
    }
  };

  const createNewForm = async (event) => {
    event.preventDefault();
    const name = newFormName.trim();
    if (!name || creating) return;

    setCreating(true);
    try {
      const response = await fetch(`${URL}/api/forms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("jwt") || ""}`,
        },
        body: JSON.stringify({
          name,
          paymentOptions: ["Numerário"],
          apiKey: "",
          info: "",
          stock: 0,
          stockOption: "UNLIMITED",
          maxQuantityOption: "UNLIMITED",
          maxQuantityPerElement: 0,
          stockDecrementOption: "PAID",
          saveReplyIp: false,
          ipMessage: "",
          latitude: 0,
          longitude: 0,
          locationUrl: "",
          sendEmailOnPaidWebhook: false,
          language: "AUTO",
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      const newForm = await response.json();
      window.location.href = `${URL_FRONT}/admin/forms/${newForm.id}/formwizard/1`;
    } catch (error) {
      setCreating(false);
      setNotice({
        type: "error",
        message: error?.message || "Não foi possível criar o formulário.",
      });
    }
  };

  const confirmDelete = () => {
    const formId = formIdToDelete;
    if (!formId) return;
    deleteForm(
      `${URL}/api/forms/${formId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
      () => {
        setFormIdToDelete(null);
        fetchForms(`${URL}/api/admin/forms`, {}, dispatch);
        setNotice({ type: "success", message: "Formulário apagado." });
      },
    );
  };

  const changeFormEmail = async (formId) => {
    try {
      const token = sessionStorage.getItem("jwt");
      const response = await fetch(
        `${URL}/api/admin/forms/${formId}/changeUser`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: emails[formId] }),
        },
      );
      if (!response.ok) throw new Error(await response.text());
      setNotice({ type: "success", message: "Responsável atualizado." });
      fetchForms(`${URL}/api/admin/forms`, {}, dispatch);
    } catch {
      setNotice({
        type: "error",
        message: "Não foi possível alterar o responsável.",
      });
    }
  };

  if (loading) return <Spinner />;

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 via-white to-emerald-50/50 px-4 py-7 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Breadcrumb pages={pages} />
        <div className="mt-6">
          <Header
            eyebrow="Administração"
            title="Formulários"
            description="Consulte todos os formulários, altere o responsável e aceda às ações de gestão."
            actions={
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-cl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-green-eupago"
              >
                <PlusIcon className="h-5 w-5" />
                Criar formulário
              </button>
            }
          />
        </div>

        <section className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-5 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <label className="relative block w-full lg:max-w-xl">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Procurar por nome, email, identificador ou chave API"
                  className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-12 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100"
                />
              </label>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className="rounded-full bg-slate-100 px-3 py-1.5 font-bold text-slate-700">
                  {filteredForms.length}
                </span>
                {filteredForms.length === 1 ? "formulário" : "formulários"}
              </div>
            </div>
          </div>

          {error && (
            <div className="m-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Não foi possível carregar os formulários.
            </div>
          )}

          {currentRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-[1050px] w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-4">Formulário</th>
                    <th className="px-6 py-4">Criado em</th>
                    <th className="px-6 py-4">Responsável</th>
                    <th className="px-6 py-4">Estado</th>
                    <th className="px-6 py-4">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentRows.map((form) => (
                    <React.Fragment key={form.id}>
                      <tr
                        className={`transition ${
                          expandedFormId === form.id
                            ? "bg-emerald-50/60"
                            : "hover:bg-emerald-50/30"
                        }`}
                      >
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <AdminFormThumbnail form={form} />
                            <div className="min-w-0">
                              <p className="max-w-xs truncate font-bold text-slate-900">
                                {form.name || "Sem nome"}
                              </p>
                              <p className="mt-1 max-w-xs truncate font-mono text-xs text-slate-400">
                                {form.id}
                              </p>
                              <p
                                className="mt-1 max-w-xs truncate font-mono text-xs font-medium text-emerald-700"
                                title={form.apiKey || ""}
                              >
                                {form.apiKey
                                  ? `Chave API: ${form.apiKey}`
                                  : "Chave API: —"}
                              </p>
                              {form.expirationDate && (
                                <p
                                  className={`mt-1 text-xs font-semibold ${
                                    isFormExpired(form)
                                      ? "text-red-700"
                                      : "text-slate-500"
                                  }`}
                                >
                                  Disponível até{" "}
                                  {formatExpirationDate(form.expirationDate)}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-5 text-sm text-slate-600">
                          {formatDate(form.timestamps?.[0])}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex min-w-[280px] items-center gap-2">
                            <input
                              type="email"
                              value={emails[form.id] || ""}
                              onChange={(event) =>
                                setEmails((current) => ({
                                  ...current,
                                  [form.id]: event.target.value,
                                }))
                              }
                              aria-label={`Responsável por ${form.name || "formulário"}`}
                              className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                            />
                            <button
                              type="button"
                              onClick={() => changeFormEmail(form.id)}
                              title="Guardar responsável"
                              className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span
                            className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${
                              isFormExpired(form)
                                ? "bg-red-50 text-red-700"
                                : form.status === "PAUSED"
                                  ? "bg-red-50 text-red-700"
                                  : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {isFormExpired(form)
                              ? "Expirado"
                              : form.status === "PAUSED"
                                ? "Bloqueado"
                                : "Ativo"}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedFormId((current) =>
                                current === form.id ? null : form.id,
                              )
                            }
                            aria-expanded={expandedFormId === form.id}
                            className={`inline-flex min-w-32 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                              expandedFormId === form.id
                                ? "bg-emerald-100 text-emerald-900"
                                : "bg-green-cl text-white hover:bg-green-eupago"
                            }`}
                          >
                            Gerir
                            <ChevronRightIcon
                              className={`h-4 w-4 transition-transform ${
                                expandedFormId === form.id ? "rotate-90" : ""
                              }`}
                            />
                          </button>
                        </td>
                      </tr>

                      {expandedFormId === form.id && (
                        <tr className="border-t border-emerald-100 bg-emerald-50/40">
                          <td colSpan={5} className="px-6 py-5">
                            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm font-extrabold text-slate-900">
                                  Ações de gestão
                                </p>
                                <p className="text-xs text-slate-500">
                                  Escolha o que pretende fazer com “
                                  {form.name || "Formulário sem nome"}”.
                                </p>
                              </div>
                            </div>

                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                              <button
                                type="button"
                                onClick={() => handleAction("public", form.id)}
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-300 hover:text-emerald-800"
                              >
                                <EyeIcon className="h-5 w-5 text-emerald-700" />
                                Ver público
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAction("fields", form.id)}
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl bg-green-cl px-4 py-3 text-left text-sm font-bold text-white shadow-sm transition hover:bg-green-eupago"
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                                Editar campos
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleAction("settings", form.id)
                                }
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-300 hover:text-emerald-800"
                              >
                                <Cog6ToothIcon className="h-5 w-5 text-emerald-700" />
                                Definições
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAction("replies", form.id)}
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-300 hover:text-emerald-800"
                              >
                                <ClipboardDocumentListIcon className="h-5 w-5 text-emerald-700" />
                                Ver respostas
                              </button>
                              <button
                                type="button"
                                onClick={() => setSharingForm(form)}
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-300 hover:text-emerald-800"
                              >
                                <LinkIcon className="h-5 w-5 text-emerald-700" />
                                Partilhar formulário
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAction("copy-id", form.id)}
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-300 hover:text-emerald-800"
                              >
                                <ClipboardDocumentIcon className="h-5 w-5 text-emerald-700" />
                                Copiar identificador
                              </button>
                              <button
                                type="button"
                                onClick={() => changeFormStatus(form)}
                                className={`inline-flex min-h-12 items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${form.status === "PAUSED" ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}
                              >
                                {form.status === "PAUSED" ? (
                                  <LockOpenIcon className="h-5 w-5" />
                                ) : (
                                  <LockClosedIcon className="h-5 w-5" />
                                )}
                                {form.status === "PAUSED"
                                  ? "Desbloquear formulário"
                                  : "Bloquear formulário"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAction("delete", form.id)}
                                className="inline-flex min-h-12 items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left text-sm font-bold text-red-700 transition hover:bg-red-100 lg:col-start-4"
                              >
                                <TrashIcon className="h-5 w-5" />
                                Apagar formulário
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-16 text-center">
              <ClipboardDocumentIcon className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-4 font-bold text-slate-800">
                Nenhum formulário encontrado
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Altere a pesquisa ou crie um novo formulário.
              </p>
            </div>
          )}

          {filteredForms.length > ROWS_PER_PAGE && (
            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
              <p className="text-sm text-slate-500">
                Página {currentPage} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => page - 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => page + 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {formIdToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                <ExclamationTriangleIcon className="h-6 w-6" />
              </span>
              <button
                type="button"
                onClick={() => setFormIdToDelete(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <h2 className="mt-5 text-xl font-extrabold text-slate-950">
              Apagar formulário?
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Esta ação remove o formulário e deixa de o disponibilizar aos
              clientes.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setFormIdToDelete(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700"
              >
                Apagar
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <form
            onSubmit={createNewForm}
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <PlusIcon className="h-6 w-6" />
              </span>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <h2 className="mt-5 text-xl font-extrabold text-slate-950">
              Novo formulário
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              O formulário só será guardado depois de indicar um nome.
            </p>
            <label
              className="mt-5 block text-sm font-bold text-slate-700"
              htmlFor="admin-new-form-name"
            >
              Nome do formulário
            </label>
            <input
              id="admin-new-form-name"
              autoFocus
              value={newFormName}
              onChange={(event) => setNewFormName(event.target.value)}
              className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
              placeholder="Ex.: Inscrição no evento"
            />
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!newFormName.trim() || creating}
                className="rounded-xl bg-green-cl px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {creating ? "A criar..." : "Criar e continuar"}
              </button>
            </div>
          </form>
        </div>
      )}

      <SharePublicLink open={Boolean(sharingForm)} onClose={() => setSharingForm(null)} title={sharingForm?.name || "Formulário"} kind="formulário" url={`${URL_FRONT}/clientforms/${sharingForm?.id || ""}`} supportOnly />

      {notice && (
        <div
          className={`fixed bottom-5 right-5 z-50 max-w-sm rounded-2xl border px-5 py-4 text-sm font-semibold shadow-xl ${
            notice.type === "error"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          <div className="flex items-center gap-3">
            <span>{notice.message}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminForms;
